import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, CalendarDays, CakeSlice, ContactRound, CreditCard, Dog as DogIcon, Mail, MapPin, Pencil, Phone, Plus, Scale, Scissors, Soup, Stethoscope, StickyNote, Trash2, UserPlus, UserRound, VenusAndMars, X, type LucideIcon } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { dogCreateSchema, type CustomerCreate, type DogCreate } from '../../../../../packages/contracts/src/index.js';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { FieldError, Input, Label, Select, Textarea } from '../../components/ui/field';
import { ActionMenu } from '../../components/ui/action-menu';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { CustomerSearchBox, SearchBox } from '../../components/ui/search';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { formatDate } from '../../shared/format';
import { copy, labelForDogStatus, labelForSex } from '../../shared/i18n';
import { useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';

const emptyStringToUndefined = (value: unknown) => (value === '' || Number.isNaN(value) ? undefined : value);

const dogInlineSchema = dogCreateSchema.omit({ customerId: true }).extend({
  birthDate: z.preprocess(emptyStringToUndefined, z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()),
  weightKg: z.preprocess(emptyStringToUndefined, z.number().positive().max(120).optional()),
});

const clientDogsSchema = z.object({
  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  phone: z.string().min(6).max(30),
  email: z.preprocess(emptyStringToUndefined, z.string().email().optional()),
  address: z.string().max(500).optional(),
  emergencyContact: z.string().max(300).optional(),
  notes: z.string().max(2000).optional(),
  dogs: z.array(dogInlineSchema).min(1),
});

type ClientDogsInput = z.input<typeof clientDogsSchema>;
type ClientDogsOutput = z.output<typeof clientDogsSchema>;

const emptyDog = {
  name: '',
  breed: '',
  birthDate: '',
  weightKg: undefined,
  sex: 'unknown' as const,
  color: '',
  vaccinationFileIds: [],
  sterilized: false,
  feedingInstructions: '',
  medicalConditions: '',
  medications: '',
  behaviorNotes: '',
  emergencyNotes: '',
  veterinarian: '',
  microchipNumber: '',
  allergies: '',
  behaviorWithDogs: '',
  behaviorWithPeople: '',
  careSpecifications: '',
  ownerComments: '',
  careStatus: 'other' as const,
  status: 'active' as const,
};

const careStatuses = [
  { value: 'danger', label: 'Peligro', className: 'border-red-300 bg-red-50 text-red-800' },
  { value: 'supervised', label: 'Supervisado', className: 'border-orange-300 bg-orange-50 text-orange-800' },
  { value: 'caution', label: 'Precaucion', className: 'border-amber-300 bg-amber-50 text-amber-800' },
  { value: 'sociable', label: 'Sociable', className: 'border-green-300 bg-green-50 text-green-800' },
  { value: 'shy', label: 'Timido', className: 'border-sky-300 bg-sky-50 text-sky-800' },
  { value: 'senior', label: 'Viejito', className: 'border-violet-300 bg-violet-50 text-violet-800' },
  { value: 'other', label: 'Otro', className: 'border-slate-300 bg-slate-50 text-slate-700' },
] as const;

export function ClientDogsPage() {
  const queryClient = useQueryClient();
  const defaults = useMemo(() => ({ q: '', ownerId: '', view: 'checked-in', tab: 'dogs', page: '1', pageSize: '10' }), []);
  const { values: filters, setValue, setValues } = useUrlListFilters(defaults);
  const search = filters.q;
  const ownerId = filters.ownerId;
  const view = filters.view as 'checked-in' | 'not-checked-in' | 'all';
  const tab = filters.tab as 'dogs' | 'clients';
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientPage, setClientPage] = useState(1);
  const form = useForm<ClientDogsInput, unknown, ClientDogsOutput>({
    resolver: zodResolver(clientDogsSchema),
    defaultValues: { firstName: '', lastName: '', phone: '', email: '', address: '', emergencyContact: '', notes: '', dogs: [emptyDog] },
  });
  const dogFields = useFieldArray({ control: form.control, name: 'dogs' });
  const customersQuery = useQuery({ queryKey: ['customers'], queryFn: () => api.customers.list({ limit: 100 }) });
  const clientCustomersQuery = useQuery({
    queryKey: ['customers', 'client-dogs-table', clientSearch.trim()],
    queryFn: () => api.customers.list({ limit: 100, search: clientSearch.trim() }),
    enabled: Boolean(clientSearch.trim()),
  });
  const dogListOptions = ownerId ? { q: search } : { limit: page * pageSize + 1, q: search };
  const dogsQuery = useQuery({
    queryKey: ownerId ? ['customer-dogs', ownerId, dogListOptions, 'client-dogs'] : ['dogs', dogListOptions, 'client-dogs'],
    queryFn: () => ownerId ? api.customers.allDogs(ownerId, dogListOptions) : api.dogs.list(dogListOptions),
  });
  const allDogsQuery = useQuery({ queryKey: ['dogs', 'client-dogs-all'], queryFn: () => api.dogs.listAll() });
  const staysQuery = useQuery({ queryKey: ['stays', 'client-dogs'], queryFn: () => api.stays.list({ limit: 100 }) });
  const daycaresQuery = useQuery({ queryKey: ['daycares', 'client-dogs'], queryFn: () => api.daycares.list({ limit: 100 }) });
  const membershipsQuery = useQuery({ queryKey: ['membership-purchases', 'client-dogs'], queryFn: () => api.memberships.purchases({ limit: 100, status: 'active' }) });
  const customers = customersQuery.data?.items ?? [];
  const dogs = dogsQuery.data?.items ?? [];
  const allDogs = allDogsQuery.data?.items ?? [];
  const clientTableCustomers = clientSearch.trim() ? clientCustomersQuery.data?.items ?? [] : customers;
  const filteredCustomers = useMemo(
    () => clientTableCustomers.filter((customer) => matchesSearch(clientSearch, [customer.firstName, customer.lastName, customer.phone, customer.email, customer.emergencyContact, customer.notes])),
    [clientSearch, clientTableCustomers],
  );
  const clientsPage = useControlledPagination(filteredCustomers, clientPage, setClientPage, 10);
  const dogCountByCustomerId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const dog of allDogs) counts.set(dog.customerId, (counts.get(dog.customerId) ?? 0) + 1);
    return counts;
  }, [allDogs]);

  const checkedInByDogId = useMemo(() => {
    const sources = new Map<string, { sourceType: 'daycare' | 'stay'; sourceId: string }>();
    for (const stay of staysQuery.data?.items ?? []) {
      if (stay.status === 'checked-in') {
        for (const dogId of stay.dogIds?.length ? stay.dogIds : [stay.dogId]) sources.set(dogId, { sourceType: 'stay', sourceId: stay.id });
      }
    }
    for (const daycare of daycaresQuery.data?.items ?? []) {
      if (daycare.status === 'checked-in') {
        for (const dogId of daycare.dogIds?.length ? daycare.dogIds : [daycare.dogId]) sources.set(dogId, { sourceType: 'daycare', sourceId: daycare.id });
      }
    }
    return sources;
  }, [daycaresQuery.data?.items, staysQuery.data?.items]);

  const filteredDogs = useMemo(() => dogs.filter((dog) => {
    const customer = customers.find((item) => item.id === dog.customerId);
    const ownerName = customer ? `${customer.firstName} ${customer.lastName}` : '';
    const checkedIn = checkedInByDogId.has(dog.id);
    if (view === 'checked-in' && !checkedIn) return false;
    if (view === 'not-checked-in' && checkedIn) return false;
    if (ownerId && dog.customerId !== ownerId) return false;
    return matchesSearch(search, [dog.name, dog.breed, dog.status, ownerName, dog.feedingInstructions, dog.medicalConditions]);
  }), [checkedInByDogId, customers, dogs, ownerId, search, view]);
  const membershipAllowanceByDogId = useMemo(() => {
    const allowances = new Map<string, { remaining: number; total: number }>();
    for (const membership of membershipsQuery.data?.items ?? []) {
      for (const allocation of membership.dogAllocations) {
        const current = allowances.get(allocation.dogId) ?? { remaining: 0, total: 0 };
        allowances.set(allocation.dogId, { remaining: current.remaining + allocation.remainingDays, total: current.total + allocation.days });
      }
    }
    return allowances;
  }, [membershipsQuery.data?.items]);
  const dogsPage = useControlledPagination(filteredDogs, page, (nextPage) => setValue('page', String(nextPage)), pageSize);

  const createClientWithDogs = useMutation({
    mutationFn: async (values: ClientDogsOutput) => {
      const customerInput: CustomerCreate = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        email: values.email,
        address: values.address || undefined,
        emergencyContact: values.emergencyContact || undefined,
        notes: values.notes || undefined,
      };
      const customer = await api.customers.create(customerInput);
      for (const dog of values.dogs) {
        const dogInput: DogCreate = {
          customerId: customer.id,
          name: dog.name,
          breed: dog.breed || undefined,
          birthDate: dog.birthDate,
          weightKg: dog.weightKg,
          sex: dog.sex,
          color: dog.color || undefined,
          feedingInstructions: dog.feedingInstructions || undefined,
          medicalConditions: dog.medicalConditions || undefined,
          medications: dog.medications || undefined,
          behaviorNotes: dog.behaviorNotes || undefined,
          emergencyNotes: dog.emergencyNotes || undefined,
          veterinarian: dog.veterinarian || undefined,
          microchipNumber: dog.microchipNumber || undefined,
          allergies: dog.allergies || undefined,
          behaviorWithDogs: dog.behaviorWithDogs || undefined,
          behaviorWithPeople: dog.behaviorWithPeople || undefined,
          careSpecifications: dog.careSpecifications || undefined,
          ownerComments: dog.ownerComments || undefined,
          vaccinationFileIds: [],
          sterilized: dog.sterilized,
          careStatus: dog.careStatus,
          status: dog.status,
        };
        await api.dogs.create(dogInput);
      }
      return customer;
    },
    onSuccess: async () => {
      form.reset({ firstName: '', lastName: '', phone: '', email: '', address: '', emergencyContact: '', notes: '', dogs: [emptyDog] });
      setCreateDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      await queryClient.invalidateQueries({ queryKey: ['dogs'] });
    },
  });
  const deleteDog = useMutation({
    mutationFn: api.dogs.remove,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dogs'] });
      await queryClient.invalidateQueries({ queryKey: ['stays'] });
      await queryClient.invalidateQueries({ queryKey: ['daycares'] });
    },
  });
  const deleteCustomer = useMutation({
    mutationFn: api.customers.remove,
    onSuccess: async () => {
      if (ownerId) setValue('ownerId', '');
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      await queryClient.invalidateQueries({ queryKey: ['dogs'] });
      await queryClient.invalidateQueries({ queryKey: ['stays'] });
      await queryClient.invalidateQueries({ queryKey: ['daycares'] });
    },
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Clientes y perros</h1>
        <p className="mt-1 text-sm text-slate-500">Alta rápida de clientes con uno o más perros y vista operativa de perros.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nuevo cliente con perros
        </Button>
        <Link className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] transition hover:bg-[#eef3ea]" to="/admin/customers/new"><UserPlus className="h-4 w-4" aria-hidden="true" />Crear cliente</Link>
        <Link className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] transition hover:bg-[#eef3ea]" to="/admin/dogs/new"><DogIcon className="h-4 w-4" aria-hidden="true" />Crear perro</Link>
      </div>

      <SegmentedControl
        value={tab}
        options={[{ id: 'dogs', label: 'Perros' }, { id: 'clients', label: 'Clientes' }]}
        onChange={(nextTab) => setValue('tab', nextTab)}
        ariaLabel="Seccion de clientes y perros"
        className="max-w-full sm:max-w-xs"
      />

      {isCreateDialogOpen ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/35 p-4" role="dialog" aria-modal="true" aria-labelledby="clientDogsDialogTitle">
          <Card className="max-h-[90vh] w-full max-w-4xl overflow-hidden">
            <CardHeader className="flex items-center justify-between gap-3">
              <h2 id="clientDogsDialogTitle" className="text-sm font-semibold">Nuevo cliente con perros</h2>
              <Button type="button" variant="ghost" className="h-8 w-8 px-0" aria-label="Cerrar" onClick={() => setCreateDialogOpen(false)}>
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </CardHeader>
            <CardContent className="max-h-[calc(90vh-4rem)] overflow-y-auto">
              <form className="grid gap-3" onSubmit={form.handleSubmit((values) => createClientWithDogs.mutate(values))}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5"><FieldIconLabel icon={UserRound} htmlFor="firstName">{copy.customers.firstName}</FieldIconLabel><Input id="firstName" {...form.register('firstName')} /><FieldError message={form.formState.errors.firstName?.message} /></div>
                  <div className="grid gap-1.5"><FieldIconLabel icon={UserRound} htmlFor="lastName">{copy.customers.lastName}</FieldIconLabel><Input id="lastName" {...form.register('lastName')} /><FieldError message={form.formState.errors.lastName?.message} /></div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5"><FieldIconLabel icon={Phone} htmlFor="phone">{copy.common.phone}</FieldIconLabel><Input id="phone" {...form.register('phone')} /><FieldError message={form.formState.errors.phone?.message} /></div>
                  <div className="grid gap-1.5"><FieldIconLabel icon={Mail} htmlFor="email">{copy.common.email}</FieldIconLabel><Input id="email" type="email" {...form.register('email')} /><FieldError message={form.formState.errors.email?.message} /></div>
                </div>
                <div className="grid gap-1.5"><FieldIconLabel icon={MapPin} htmlFor="address">{copy.customers.address}</FieldIconLabel><Input id="address" {...form.register('address')} /></div>
                <div className="grid gap-1.5"><FieldIconLabel icon={ContactRound} htmlFor="emergencyContact">{copy.customers.emergencyContact}</FieldIconLabel><Input id="emergencyContact" {...form.register('emergencyContact')} /></div>
                <div className="grid gap-1.5"><FieldIconLabel icon={StickyNote} htmlFor="notes">{copy.customers.notes}</FieldIconLabel><Textarea id="notes" {...form.register('notes')} /></div>

                <div className="mt-2 grid gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-[#334033]">Perros</h3>
                    <Button type="button" variant="secondary" onClick={() => dogFields.append(emptyDog)}>
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Agregar perro
                    </Button>
                  </div>
                  {dogFields.fields.map((field, index) => (
                    <div key={field.id} className="grid gap-3 rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-[#334033]">Perro {index + 1}</p>
                        {dogFields.fields.length > 1 ? (
                          <Button type="button" variant="danger" className="h-8 w-8 px-0" aria-label="Quitar perro" onClick={() => dogFields.remove(index)}>
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid gap-1.5"><FieldIconLabel icon={DogIcon} htmlFor={`dogs.${index}.name`}>{copy.common.name}</FieldIconLabel><Input id={`dogs.${index}.name`} {...form.register(`dogs.${index}.name`)} /><FieldError message={form.formState.errors.dogs?.[index]?.name?.message} /></div>
                        <div className="grid gap-1.5"><FieldIconLabel icon={DogIcon} htmlFor={`dogs.${index}.breed`}>{copy.dogs.breed}</FieldIconLabel><Input id={`dogs.${index}.breed`} {...form.register(`dogs.${index}.breed`)} /></div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid gap-1.5"><FieldIconLabel icon={CakeSlice} htmlFor={`dogs.${index}.birthDate`}>{copy.dogs.birthDate}</FieldIconLabel><Input id={`dogs.${index}.birthDate`} type="date" {...form.register(`dogs.${index}.birthDate`)} /><FieldError message={form.formState.errors.dogs?.[index]?.birthDate?.message} /></div>
                        <div className="grid gap-1.5"><FieldIconLabel icon={Scale} htmlFor={`dogs.${index}.weightKg`}>{copy.dogs.weightKg}</FieldIconLabel><Input id={`dogs.${index}.weightKg`} type="number" step="0.1" {...form.register(`dogs.${index}.weightKg`, { valueAsNumber: true })} /></div>
                        <div className="grid gap-1.5"><FieldIconLabel icon={VenusAndMars} htmlFor={`dogs.${index}.sex`}>{copy.dogs.sex}</FieldIconLabel><Select id={`dogs.${index}.sex`} {...form.register(`dogs.${index}.sex`)}><option value="unknown">{labelForSex('unknown')}</option><option value="male">{labelForSex('male')}</option><option value="female">{labelForSex('female')}</option></Select></div>
                        <div className="grid gap-1.5"><FieldIconLabel icon={DogIcon} htmlFor={`dogs.${index}.color`}>Color</FieldIconLabel><Input id={`dogs.${index}.color`} {...form.register(`dogs.${index}.color`)} /></div>
                      </div>
                      <div className="grid gap-1.5">
                        <Label htmlFor={`dogs.${index}.status`}>{copy.common.status}</Label>
                        <Select id={`dogs.${index}.status`} {...form.register(`dogs.${index}.status`)}>
                          <option value="active">{labelForDogStatus('active')}</option>
                          <option value="inactive">{labelForDogStatus('inactive')}</option>
                          <option value="deceased">{labelForDogStatus('deceased')}</option>
                        </Select>
                      </div>
                      <fieldset className="grid gap-2"><legend className="text-sm font-medium text-[#4f5d49]">Estatus de cuidado</legend><div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">{careStatuses.map((status) => <label key={status.value} className={`cursor-pointer rounded-md border p-2 text-center text-xs font-semibold ${status.className} ${form.watch(`dogs.${index}.careStatus`) === status.value ? 'ring-2 ring-[#223126] ring-offset-1' : ''}`}><input className="sr-only" type="radio" value={status.value} {...form.register(`dogs.${index}.careStatus`)} />{status.label}</label>)}</div></fieldset>
                      <label className="flex items-center gap-2 text-sm font-medium text-[#4f5d49]"><input type="checkbox" {...form.register(`dogs.${index}.sterilized`)} />Esterilizado</label>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid gap-1.5"><FieldIconLabel icon={Soup} htmlFor={`dogs.${index}.feedingInstructions`}>{copy.dogs.feedingInstructions}</FieldIconLabel><Textarea id={`dogs.${index}.feedingInstructions`} {...form.register(`dogs.${index}.feedingInstructions`)} /></div>
                        <div className="grid gap-1.5"><FieldIconLabel icon={Stethoscope} htmlFor={`dogs.${index}.medicalConditions`}>{copy.dogs.medicalConditions}</FieldIconLabel><Textarea id={`dogs.${index}.medicalConditions`} {...form.register(`dogs.${index}.medicalConditions`)} /></div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.medications`}>Medicamentos</Label><Textarea id={`dogs.${index}.medications`} {...form.register(`dogs.${index}.medications`)} /></div>
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.allergies`}>Alergias</Label><Textarea id={`dogs.${index}.allergies`} {...form.register(`dogs.${index}.allergies`)} /></div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.behaviorWithDogs`}>Comportamiento con otros perros</Label><Textarea id={`dogs.${index}.behaviorWithDogs`} {...form.register(`dogs.${index}.behaviorWithDogs`)} /></div>
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.behaviorWithPeople`}>Comportamiento con personas</Label><Textarea id={`dogs.${index}.behaviorWithPeople`} {...form.register(`dogs.${index}.behaviorWithPeople`)} /></div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.behaviorNotes`}>Notas de comportamiento</Label><Textarea id={`dogs.${index}.behaviorNotes`} {...form.register(`dogs.${index}.behaviorNotes`)} /></div>
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.emergencyNotes`}>Notas de emergencia</Label><Textarea id={`dogs.${index}.emergencyNotes`} {...form.register(`dogs.${index}.emergencyNotes`)} /></div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.veterinarian`}>Veterinario</Label><Input id={`dogs.${index}.veterinarian`} {...form.register(`dogs.${index}.veterinarian`)} /></div>
                        <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.microchipNumber`}>Microchip</Label><Input id={`dogs.${index}.microchipNumber`} {...form.register(`dogs.${index}.microchipNumber`)} /></div>
                      </div>
                      <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.careSpecifications`}>Especificaciones para el cuidado</Label><Textarea id={`dogs.${index}.careSpecifications`} {...form.register(`dogs.${index}.careSpecifications`)} /></div>
                      <div className="grid gap-1.5"><Label htmlFor={`dogs.${index}.ownerComments`}>Comentarios del propietario</Label><Textarea id={`dogs.${index}.ownerComments`} {...form.register(`dogs.${index}.ownerComments`)} /></div>
                    </div>
                  ))}
                </div>
                {createClientWithDogs.isError ? <p className="text-sm text-rose-600">{createClientWithDogs.error.message}</p> : null}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="secondary" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={createClientWithDogs.isPending}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Crear cliente y perros
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : null}

      <section className="grid gap-5">
        <Card className={tab === 'dogs' ? undefined : 'hidden'}>
          <CardHeader className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[#223126]">Perros</h2>
                <p className="mt-1 text-sm text-[#687461]">Consulta el estado y la informacion operativa de cada perro.</p>
              </div>
              <div className="grid min-w-0 gap-1.5">
                <span className="px-1 text-[10px] font-semibold uppercase tracking-wide text-[#687461]">Mostrar</span>
                <SegmentedControl
                  value={view}
                  options={[{ id: 'checked-in', label: 'Checked in' }, { id: 'not-checked-in', label: 'Sin check-in' }, { id: 'all', label: 'Todos' }]}
                  onChange={(nextView) => setValue('view', nextView)}
                  ariaLabel="Mostrar perros"
                />
              </div>
            </div>
            <div className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
              <div className="mb-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4f5d49]">Filtros de perros</p>
                <p className="mt-1 text-xs text-[#687461]">Filtra por cliente o busca por nombre, raza y estado.</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label>Cliente</Label>
                  <CustomerSearchBox id="dog-owner-filter" value={ownerId} customers={customers} placeholder="Selecciona un cliente" onChange={(customerId) => setValues({ ownerId: customerId, view: customerId ? 'all' : view })} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Buscar perro</Label>
                  <SearchBox value={search} onChange={(value) => setValue('q', value)} placeholder="Nombre, raza o estado" label="Buscar perro" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {customersQuery.isLoading || dogsQuery.isLoading || allDogsQuery.isLoading || staysQuery.isLoading || daycaresQuery.isLoading || membershipsQuery.isLoading ? <LoadingState label="Cargando clientes y perros" /> : null}
            {customersQuery.isError ? <ErrorState message={customersQuery.error.message} /> : null}
            {clientCustomersQuery.isError ? <ErrorState message={clientCustomersQuery.error.message} /> : null}
            {dogsQuery.isError ? <ErrorState message={dogsQuery.error.message} /> : null}
            {allDogsQuery.isError ? <ErrorState message={allDogsQuery.error.message} /> : null}
            {staysQuery.isError ? <ErrorState message={staysQuery.error.message} /> : null}
            {daycaresQuery.isError ? <ErrorState message={daycaresQuery.error.message} /> : null}
            {membershipsQuery.isError ? <ErrorState message={membershipsQuery.error.message} /> : null}
            {dogsQuery.data && filteredDogs.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro cliente, perro o vista." /> : null}
            {filteredDogs.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <Th>{copy.stays.dog}</Th>
                      <Th>{copy.stays.customer}</Th>
                      <Th>{copy.dogs.breed}</Th>
                      <Th>Membresia</Th>
                      <Th>{copy.common.status}</Th>
                      <Th>{copy.common.actions}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dogsPage.pageItems.map((dog) => {
                      const customer = customers.find((item) => item.id === dog.customerId);
                      const source = checkedInByDogId.get(dog.id);
                      const paymentUrl = source ? `/admin/payments?customerId=${encodeURIComponent(dog.customerId)}&sourceType=${source.sourceType}&sourceId=${encodeURIComponent(source.sourceId)}` : '/admin/payments';
                      return (
                        <tr key={dog.id} className={dog.status === 'deceased' ? 'bg-[#f7f0ee] text-[#8a4a3a]' : undefined}>
                          <Td className="font-medium text-slate-900">
                            <span className="inline-flex items-center gap-2">
                              <DogIcon className={`h-4 w-4 ${dog.status === 'deceased' ? 'text-[#8a4a3a]' : 'text-[#75816a]'}`} aria-hidden="true" />
                              <Link className={`${dog.status === 'deceased' ? 'text-[#8a4a3a]' : 'text-[#2f6b38]'} hover:underline`} to={`/admin/dogs/${dog.id}/edit`}>{dog.name}</Link>
                              {dog.status === 'deceased' ? <Badge className="bg-[#eadbd6] text-[#8a4a3a]">Fallecido</Badge> : null}
                            </span>
                          </Td>
                          <Td>{customer ? <Link className="text-[#2f6b38] hover:underline" to={`/admin/customers/${customer.id}/edit`}>{customer.firstName} {customer.lastName}</Link> : dog.customerId}</Td>
                          <Td>{dog.breed ?? copy.common.unknown}</Td>
                          <Td>{membershipAllowanceByDogId.has(dog.id) ? `${membershipAllowanceByDogId.get(dog.id)?.remaining ?? 0}/${membershipAllowanceByDogId.get(dog.id)?.total ?? 0} dias` : 'Sin membresia'}</Td>
                          <Td><Badge>{source ? 'Checked in' : labelForDogStatus(dog.status)}</Badge></Td>
                          <Td>
                            <ActionMenu groups={[
                              {
                                label: 'Perro',
                                children: <>
                                  <MenuLink to={`/admin/dogs/${dog.id}/edit`} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar perro</MenuLink>
                                  <MenuButton danger disabled={deleteDog.isPending} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />} onClick={() => setConfirm({ title: 'Eliminar perro', body: copy.dogs.deleteConfirm(dog.name), confirmLabel: 'Eliminar', onConfirm: () => deleteDog.mutate(dog.id) })}>Eliminar perro</MenuButton>
                                </>,
                              },
                              {
                                label: 'Cliente',
                                children: <>
                                  <MenuLink to={`/admin/customers/${dog.customerId}/edit`} disabled={!customer} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar cliente</MenuLink>
                                  <MenuButton danger disabled={!customer || deleteCustomer.isPending} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />} onClick={() => customer && setConfirm({ title: 'Eliminar cliente', body: `${copy.customers.deleteConfirm(`${customer.firstName} ${customer.lastName}`)} Sus perros permanecerán registrados.`, confirmLabel: 'Eliminar', onConfirm: () => deleteCustomer.mutate(customer.id) })}>Eliminar cliente</MenuButton>
                                </>,
                              },
                              {
                                label: 'Reservaciones',
                                children: <>
                                  <MenuLink to={`/admin/daycares/new?customerId=${encodeURIComponent(dog.customerId)}&dogId=${encodeURIComponent(dog.id)}`} icon={<CalendarCheck className="h-4 w-4" aria-hidden="true" />}>Agregar guarderia</MenuLink>
                                  <MenuLink to={`/admin/stays/new?customerId=${encodeURIComponent(dog.customerId)}&dogId=${encodeURIComponent(dog.id)}`} icon={<CalendarDays className="h-4 w-4" aria-hidden="true" />}>Agregar hotel</MenuLink>
                                  <MenuLink to={`/admin/grooming/new?customerId=${encodeURIComponent(dog.customerId)}&dogId=${encodeURIComponent(dog.id)}`} icon={<Scissors className="h-4 w-4" aria-hidden="true" />}>Agregar cita</MenuLink>
                                </>,
                              },
                              {
                                label: 'Pago',
                                children: <MenuLink to={paymentUrl} disabled={!source} icon={<CreditCard className="h-4 w-4" aria-hidden="true" />}>Agregar al carrito</MenuLink>,
                              },
                            ]} />
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : null}
            <PaginationControls {...dogsPage} onPageChange={dogsPage.setPage} onPageSizeChange={(nextPageSize) => setValue('pageSize', String(nextPageSize))} />
            {deleteDog.isError ? <p className="mt-3 text-sm text-rose-600">{deleteDog.error.message}</p> : null}
            {deleteCustomer.isError ? <p className="mt-3 text-sm text-rose-600">{deleteCustomer.error.message}</p> : null}
          </CardContent>
        </Card>
        <Card className={tab === 'clients' ? undefined : 'hidden'}>
          <CardHeader className="space-y-4">
            <div className="grid gap-3">
              <div>
                <h2 className="text-base font-semibold text-[#223126]">Clientes</h2>
                <p className="mt-1 text-xs text-[#687461]">También puedes gestionar clientes sin perros registrados.</p>
              </div>
              <div className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
                <div className="mb-2">
                  <Label>Buscar cliente</Label>
                  <p className="mt-1 text-xs text-[#687461]">Busca por nombre, telefono o correo.</p>
                </div>
                <SearchBox value={clientSearch} onChange={(value) => { setClientSearch(value); setClientPage(1); }} placeholder="Nombre, telefono o correo" label="Buscar cliente" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredCustomers.length === 0 ? <EmptyState title="Sin clientes" body="Intenta con otro nombre, teléfono o correo." /> : (
              <div className="overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <Th>Cliente</Th>
                      <Th>Contacto</Th>
                      <Th>Perros</Th>
                      <Th>{copy.common.actions}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientsPage.pageItems.map((customer) => {
                      const dogCount = dogCountByCustomerId.get(customer.id) ?? 0;
                      return (
                        <tr key={customer.id}>
                          <Td className="font-medium text-slate-900">
                            <span className="inline-flex items-center gap-2">
                              <UserRound className="h-4 w-4 text-[#75816a]" aria-hidden="true" />
                              <Link className="text-[#2f6b38] hover:underline" to={`/admin/customers/${customer.id}/edit`}>{customer.firstName} {customer.lastName}</Link>
                            </span>
                          </Td>
                          <Td>{customer.phone ?? customer.email ?? copy.common.unknown}</Td>
                          <Td>{dogCount > 0 ? dogCount : 'Sin perros'}</Td>
                          <Td>
                            <ActionMenu groups={[{
                              label: 'Cliente',
                              children: <>
                                <MenuLink to={`/admin/customers/${customer.id}/edit`} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar cliente</MenuLink>
                                <MenuButton danger disabled={deleteCustomer.isPending} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />} onClick={() => setConfirm({ title: 'Eliminar cliente', body: `${copy.customers.deleteConfirm(`${customer.firstName} ${customer.lastName}`)} Sus perros permanecerán registrados.`, confirmLabel: 'Eliminar', onConfirm: () => deleteCustomer.mutate(customer.id) })}>Eliminar cliente</MenuButton>
                              </>,
                            }]} />
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
            <PaginationControls {...clientsPage} onPageChange={clientsPage.setPage} onPageSizeChange={() => setClientPage(1)} />
          </CardContent>
        </Card>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function FieldIconLabel({ icon: Icon, htmlFor, children }: { readonly icon: LucideIcon; readonly htmlFor?: string; readonly children: ReactNode }) {
  return <Label htmlFor={htmlFor}><span className="inline-flex items-center gap-1.5"><Icon className="h-4 w-4 text-[#75816a]" aria-hidden="true" />{children}</span></Label>;
}

function MenuLink({ to, disabled, icon, children }: { readonly to: string; readonly disabled?: boolean; readonly icon: React.ReactNode; readonly children: React.ReactNode }) {
  return <Link className={`flex items-center gap-2 rounded-md px-2 py-2 ${disabled ? 'pointer-events-none text-[#9aa392]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} to={to}>{icon}{children}</Link>;
}

function MenuButton({ children, danger, disabled, icon, onClick }: { readonly children: ReactNode; readonly danger?: boolean; readonly disabled?: boolean; readonly icon: ReactNode; readonly onClick: () => void }) {
  return <button type="button" className={`flex items-center gap-2 rounded-md px-2 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${danger ? 'text-[#8a4a3a] hover:bg-[#fff8f8]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} disabled={disabled} onClick={onClick}>{icon}{children}</button>;
}
